import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowDown, PanelRightClose, PanelRightOpen } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Composer } from "@/components/conversation";
import { TaskRail } from "@/components/task-rail";
import { Thread } from "@/components/thread";
import { Spinner } from "@/components/ui";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useStickToBottom } from "@/hooks/use-auto-scroll";
import {
  api,
  type Task,
  type TaskEvent,
  type TranscriptEntry,
} from "@/lib/api";
import { useTaskStream } from "@/lib/live";
import { cn, money, stateClass, stateLabel } from "@/lib/states";

/**
 * The ask workspace, ChatGPT-shaped: one full-height chat column (the durable
 * conversation and the agent's transcript woven into a single time-ordered
 * feed, à la Claude Code / OpenCode) with the composer fixed at its foot and
 * a collapsible Coinbase-dense task rail on the right (state machine, budget,
 * runs, PRs). ONE scroll container owns the thread; new content sticks to the
 * bottom unless the reader scrolled up, and a "jump to latest" pill brings
 * them back.
 */

/** Accumulate a task's events + transcript: seed via REST, follow via SSE. */
function useTaskFeed(taskId: string | undefined) {
  const [events, setEvents] = useState<TaskEvent[]>([]);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const seeded = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!taskId || seeded.current === taskId) return;
    seeded.current = taskId;
    setEvents([]);
    setTranscript([]);
    void api.taskEvents(taskId, 0).then(({ events }) => {
      setEvents((current) => mergeBySeq(current, events));
    });
    void api.taskTranscript(taskId, 0).then(({ entries }) => {
      setTranscript((current) => mergeBySeq(current, entries));
    });
  }, [taskId]);

  useTaskStream(taskId, {
    onEvent: (event) => setEvents((current) => mergeBySeq(current, [event])),
    onTranscript: (entry) =>
      setTranscript((current) => mergeBySeq(current, [entry])),
  });

  return { events, transcript };
}

function mergeBySeq<T extends { seq: number }>(current: T[], added: T[]): T[] {
  if (added.length === 0) return current;
  const seen = new Set(current.map((e) => e.seq));
  const fresh = added.filter((e) => !seen.has(e.seq));
  if (fresh.length === 0) return current;
  return [...current, ...fresh].sort((a, b) => a.seq - b.seq);
}

/** The task the pane focuses: the live one, else the newest. */
function focusTask(tasks: Task[]): Task | undefined {
  return (
    tasks.find(
      (t) => !["completed", "failed", "cancelled"].includes(t.state),
    ) ?? tasks[0]
  );
}

function ConversationPage() {
  const { conversationId } = Route.useParams();
  const [railOpen, setRailOpen] = useState(true);

  const detail = useQuery({
    queryKey: ["conversation", conversationId],
    queryFn: () => api.conversation(conversationId),
    refetchInterval: 15_000, // SSE invalidation is the fast path.
  });
  const repos = useQuery({ queryKey: ["repos"], queryFn: api.repos });

  const task = detail.data ? focusTask(detail.data.tasks) : undefined;
  const { events, transcript } = useTaskFeed(task?.id);

  const taskDetail = useQuery({
    queryKey: ["task", task?.id],
    queryFn: () => api.task(task?.id ?? ""),
    enabled: task !== undefined,
  });
  const pendingApprovals = useMemo(
    () =>
      (taskDetail.data?.approvals ?? []).filter((a) => a.status === "pending"),
    [taskDetail.data],
  );

  // Follow the thread as either feed grows (messages + transcript).
  const threadGrowth = (detail.data?.messages.length ?? 0) + transcript.length;
  const {
    ref: scrollRef,
    stuck,
    jumpToBottom,
  } = useStickToBottom<HTMLDivElement>(threadGrowth);

  return (
    <div className="flex h-full min-h-0 flex-col bg-bg">
      <header className="flex h-12 shrink-0 items-center gap-3 border-edge border-b px-3">
        <SidebarTrigger className="-ml-1" />
        {detail.data && (
          <>
            <h1 className="truncate font-medium text-sm">
              {detail.data.conversation.title}
            </h1>
            {task && (
              <span
                className={cn(
                  "rounded-full border px-2 py-0.5 text-[11px]",
                  stateClass(task.state),
                )}
              >
                {stateLabel(task.state)}
              </span>
            )}
            <span className="tabular-nums text-muted text-xs">
              {money(detail.data.totalSpendUsd)} spent
            </span>
            <button
              type="button"
              onClick={() => setRailOpen((o) => !o)}
              className="ml-auto rounded-lg p-1.5 text-muted hover:bg-panel hover:text-ink"
              title={railOpen ? "Hide task panel" : "Show task panel"}
            >
              {railOpen ? (
                <PanelRightClose className="size-4" />
              ) : (
                <PanelRightOpen className="size-4" />
              )}
            </button>
          </>
        )}
      </header>

      <div className="flex min-h-0 flex-1">
        <main className="relative flex min-w-0 flex-1 flex-col">
          {detail.isLoading && (
            <div className="grid flex-1 place-items-center">
              <Spinner />
            </div>
          )}
          {detail.data && (
            <>
              <div
                ref={scrollRef}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
              >
                <Thread
                  detail={detail.data}
                  entries={transcript}
                  repos={repos.data?.repos ?? []}
                  pendingApprovals={pendingApprovals}
                />
              </div>
              {!stuck && (
                <button
                  type="button"
                  onClick={jumpToBottom}
                  className="-translate-x-1/2 absolute bottom-24 left-1/2 z-10 flex items-center gap-1 rounded-full border border-edge bg-bg px-3 py-1.5 text-muted text-xs shadow-md hover:text-ink"
                >
                  <ArrowDown className="size-3.5" />
                  Jump to latest
                </button>
              )}
              <Composer detail={detail.data} />
            </>
          )}
        </main>

        {railOpen && detail.data && (
          <aside className="hidden w-80 shrink-0 overflow-y-auto border-edge border-l bg-panel/40 lg:block">
            {task ? (
              <TaskRail task={task} events={events} />
            ) : (
              <p className="p-4 text-muted text-sm">
                No task yet — the conversation is warming up.
              </p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/c/$conversationId")({
  component: ConversationPage,
});
